import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateCart } from '../../hooks/useCreateCart';

const CreateCartModal = forwardRef<ModalCenterRef>((_, ref) => {
  const { t } = useTranslation('cartsModule');
  const { toast } = useToast();
  const createCartMutation = useCreateCart();

  const [code, setCode] = useState('');

  const handleCancel = () => {
    (ref as React.RefObject<ModalCenterRef>)?.current?.closeModal();
    // 🎯 Limpiar formulario al cancelar
    resetForm();
  };

  const resetForm = () => {
    setCode('');
  };

  const handleCreate = async () => {
    try {
      if (!code) {
        toast({
          title: t('errors.generic'),
          description: t('modal.create.error_required'),
          variant: 'destructive',
        });
        return;
      }

      await createCartMutation.mutateAsync({
        code: code.trim(),
      });

      toast({
        title: t('modal.create.success_title'),
        description: t('modal.create.success_description'),
      });

      resetForm();
      handleCancel();
    } catch (error: any) {
      toast({
        title: t('modal.create.error_title'),
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    }
  };

  const isLoading = createCartMutation.isPending;

  return (
    <ModalCenter
      ref={ref}
      title={t('modal.create.title')}
      type="default"
      width="lg"
      className="h-[calc(100%-30px)] sm:h-auto"
    >
      <div className="space-y-4 flex flex-col justify-between h-full">
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-text">{t('modal.create.code_label')}</label>
            <Input
              className="mt-1"
              placeholder={t('modal.create.code_placeholder')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleCancel} name="cancel" size="lg" disabled={isLoading}>
            {t('button.cancel')}
          </Button>
          <Button variant="primary" onClick={handleCreate} name="confirm" size="lg" disabled={isLoading}>
            {isLoading ? t('button.creating') : t('button.create')}
          </Button>
        </div>
      </div>
    </ModalCenter>
  );
});

export default CreateCartModal;
CreateCartModal.displayName = 'CreateCartModal';
